import { useCallback, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useDataStore } from '@/stores/dataStore';
import { useAuthStore } from '@/stores/authStore';
import { useDmFolders, orderChannelsByFolder, countUnreadChats } from '@/lib/useDmFolders';
import { useDmFolderPrefs } from '@/lib/dmFolderPrefs';
import { useFriends } from '@/lib/useFriends';
import { emitAppEvent } from '@/lib/appEvents';
import { scrollHorizontallyOnWheel } from '@/lib/scrollHorizontallyOnWheel';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { errToToast } from '@/lib/humanizeApiError';
import { directChannelLabel, SavedShortcut, isSelfVault } from '@/components/channelDrawer/primitives';
import { messagePreviewText, shortTimeLabel } from '@/lib/messagePreview';
import { SettingsIcon, UsersIcon, ChatBubbleIcon, CloseIcon } from '@/components/icons';
import { useTranslation } from 'react-i18next';
import { DmFolderContextMenu } from './DmFolderContextMenu';
import { FolderManageMenu } from './FolderManageMenu';
import { DmRow } from './dmList/DmRow';
import { latestMessage } from './dmList/latestMessage';
import { selectDmMentions, selectDmNewest, selectDmTyping, selectDmUnread } from './dmList/dmListSelectors';
import {
  CreateFolderButton,
  FolderChip,
  FriendRow,
  FriendsSection,
  RowIconButton,
} from './dmList/sidebarParts';
import type { DirectChannel, UserStatus } from '@/types/api';
import { Tooltip } from '@/components/Tooltip';


/**
 * Direct-message list for the HeroUI sidebar with Telegram-style folder tabs.
 *
 * A strip of folder chips ("Все" + each custom folder + a "＋" create popover
 * + a settings cog) sits either above or to the left of the list (controlled
 * by the per-device {@link useDmFolderPrefs} `tabPlacement`). Each chip carries
 * an unread-chats badge. Selecting a folder filters the list to that folder's
 * channels (in the folder's own order). Right-clicking a row manages its folder
 * membership; right-clicking a chip renames/deletes the folder. When the
 * `showTags` pref is on, each row shows its folder-name tags.
 */
export function HeroDmList() {
  const { t } = useTranslation();
  const dmOrder = useDataStore((s) => s.dmOrder);
  const dmChannels = useDataStore((s) => s.dmChannels);
  const currentChannelId = useDataStore((s) => s.currentChannelId);
  const selectDirectChannel = useDataStore((s) => s.selectDirectChannel);
  const upsertDmChannel = useDataStore((s) => s.upsertDmChannel);
  const myId = useAuthStore((s) => s.user?.id ?? null);
  // Per-DM reads only: guild traffic (messages, typing, unread) must not
  // re-render this list.
  const newestByDm = useDataStore(useShallow(selectDmNewest));
  const typingSelector = useMemo(() => selectDmTyping(myId), [myId]);
  const typingByDm = useDataStore(useShallow(typingSelector));
  const unreadByChannel = useDataStore(useShallow(selectDmUnread));
  const mentionByChannel = useDataStore(useShallow(selectDmMentions));
  const { folders } = useDmFolders();
  const prefs = useDmFolderPrefs();
  const { friends, incoming, outgoing, accept, remove, pendingId, incomingCount } = useFriends();

  // Open (or create) the 1:1 DM with a friend, then select it.
  async function openDmWith(userId: string) {
    try {
      const channel = await api.openDirectMessage(userId);
      upsertDmChannel(channel);
      selectDirectChannel(channel.id);
    } catch (err) {
      toast.error(...errToToast(err, t('dm.openError')));
    }
  }

  // Friends sorted online-first, then by name — a Discord-style roster under
  // the conversation list. Shown only on the "Все" tab (folders narrow to
  // foldered DMs, where a full friends roster would be off-topic).
  const sortedFriends = useMemo(() => {
    const online = (s: UserStatus | undefined) => s && s !== 'OFFLINE' && s !== 'INVISIBLE';
    return [...friends].sort((a, b) => {
      const ao = online(a.user.status) ? 0 : 1;
      const bo = online(b.user.status) ? 0 : 1;
      if (ao !== bo) return ao - bo;
      const an = (a.user.displayName || a.user.username).toLowerCase();
      const bn = (b.user.displayName || b.user.username).toLowerCase();
      return an.localeCompare(bn);
    });
  }, [friends]);

  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  // The built-in "Друзья" tab (friend requests + roster) sits next to "Все".
  // It's not a real channel folder — friends without a DM have no channel —
  // so it's a special view toggled independently of the folder selection.
  const [friendsTab, setFriendsTab] = useState(false);
  const [menu, setMenu] = useState<{ channelId: string; x: number; y: number } | null>(null);
  // Stable handler so memoized <DmRow>s aren't invalidated every render. The
  // row passes its own channelId back in. (selectDirectChannel is already a
  // stable store action, so it can be handed to the row directly.)
  const openMenuFor = useCallback(
    (channelId: string, x: number, y: number) => setMenu({ channelId, x, y }),
    [],
  );
  const [folderMenu, setFolderMenu] = useState<{
    id: string;
    name: string;
    x: number;
    y: number;
  } | null>(null);

  // All DMs in sidebar order. The personal "Сейф" (self-chat) is pulled out and
  // pinned at the top via SavedShortcut, so it never appears as an ordinary DM.
  const rawItems = useMemo(
    () => dmOrder.map((id) => dmChannels[id]).filter((c): c is DirectChannel => !!c),
    [dmOrder, dmChannels],
  );
  const vault = useMemo(() => rawItems.find((c) => isSelfVault(c, myId)) ?? null, [rawItems, myId]);
  const allItems = useMemo(() => rawItems.filter((c) => !isSelfVault(c, myId)), [rawItems, myId]);

  // Open (or create on first use) the personal Сейф, then select it.
  async function openVault() {
    try {
      const ch = await api.openSavedVault();
      upsertDmChannel(ch);
      selectDirectChannel(ch.id);
    } catch (err) {
      toast.error(...errToToast(err, t('dm.openError')));
    }
  }

  const activeFolder = activeFolderId
    ? folders.find((f) => f.id === activeFolderId) ?? null
    : null;
  // If the active folder was deleted elsewhere, fall back to "Все".
  const effectiveFolder = activeFolderId && !activeFolder ? null : activeFolder;

  const items = useMemo(
    () =>
      effectiveFolder
        ? orderChannelsByFolder(allItems, effectiveFolder.channelIds)
        : allItems,
    [effectiveFolder, allItems],
  );

  // channelId -> folder names it belongs to (for the "Теги для чатов" pref).
  const tagsByChannel = useMemo(() => {
    const map = new Map<string, string[]>();
    if (!prefs.showTags) return map;
    for (const f of folders) {
      for (const id of f.channelIds) {
        const arr = map.get(id);
        if (arr) arr.push(f.name);
        else map.set(id, [f.name]);
      }
    }
    return map;
  }, [folders, prefs.showTags]);

  const allUnread = countUnreadChats(
    allItems.map((c) => c.id),
    unreadByChannel,
  );

  // "Все" + "Друзья" are always present, so the strip always renders.
  const isLeft = prefs.tabPlacement === 'left';

  // The folder chips (scrollable). "Все" and "Друзья" are built-in; user
  // folders follow. On top placement this lives in a horizontal scroller so
  // many folders swipe sideways instead of wrapping the +/⚙ icons to a new row.
  const chips = (
    <>
      <FolderChip
        label={t('dm.folderAll')}
        active={!friendsTab && effectiveFolder === null}
        count={allUnread}
        vertical={isLeft}
        onClick={() => {
          setFriendsTab(false);
          setActiveFolderId(null);
        }}
      />
      <FolderChip
        label={t('dm.friendsTab')}
        active={friendsTab}
        count={incomingCount}
        danger
        vertical={isLeft}
        onClick={() => {
          setFriendsTab(true);
          setActiveFolderId(null);
        }}
      />
      {folders.map((f) => (
        <FolderChip
          key={f.id}
          label={f.name}
          active={!friendsTab && effectiveFolder?.id === f.id}
          count={countUnreadChats(f.channelIds, unreadByChannel)}
          vertical={isLeft}
          onClick={() => {
            setFriendsTab(false);
            setActiveFolderId(f.id);
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            setFolderMenu({ id: f.id, name: f.name, x: e.clientX, y: e.clientY });
          }}
          onContextMenuAt={(x, y) => {
            setFolderMenu({ id: f.id, name: f.name, x, y });
          }}
        />
      ))}
    </>
  );

  const stripIcons = (
    <div className={isLeft ? 'flex items-center justify-center gap-1 pt-0.5' : 'flex shrink-0 items-center gap-1'}>
      <CreateFolderButton />
      <Tooltip label={t('dm.folderSettings')}>
        <button
          type="button"
          aria-label={t('dm.folderSettings')}
          onClick={() => emitAppEvent('dm-folders:manage')}
          className="group grid size-11 place-items-center rounded-lg text-muted transition-colors elev-hover hover:text-foreground/90"
        >
          <SettingsIcon className="h-4 w-4 transition-transform duration-700 ease-out group-hover:rotate-[360deg]" />
        </button>
      </Tooltip>
    </div>
  );

  const strip = isLeft ? (
    <div className="flex w-[88px] shrink-0 flex-col gap-1 overflow-y-auto border-r border-separator px-1.5 py-1">
      {chips}
      {stripIcons}
    </div>
  ) : (
    <div className="studio-dm-tabs mb-1.5 flex items-center gap-1 px-1">
      <div
        className="no-scrollbar flex min-w-0 flex-1 items-center gap-1 overflow-x-auto"
        // Mouse wheels only scroll vertically; translate that to horizontal so
        // desktop users can reach folders past the right edge (touch/trackpad
        // already swipe sideways).
        onWheel={scrollHorizontallyOnWheel}
      >
        {chips}
      </div>
      {stripIcons}
    </div>
  );

  const list =
    items.length === 0 ? (
      <p className="px-3 py-6 text-center text-sm text-muted">
        {effectiveFolder ? t('dm.emptyFolder') : t('dm.emptyDms')}
      </p>
    ) : (
      <nav aria-label={t('dm.title')} className="flex min-w-0 flex-1 flex-col gap-0.5">
        {items.map((ch) => {
          const label = directChannelLabel(ch, myId);
          const active = ch.id === currentChannelId;
          const other =
            ch.type === 'DM'
              ? ch.directParticipants.find((p) => p.userId !== myId)
              : null;
          const url = ch.type === 'GROUP_DM' ? ch.iconUrl : other?.user.avatarUrl;
          const last = latestMessage(ch, newestByDm[ch.id]);
          // Prefix the author for group DMs always, and for 1:1 DMs only when
          // the last message is mine ("Вы: …"); a peer's message in a 1:1
          // needs no name since the row already names them.
          const withAuthor = ch.type === 'GROUP_DM' || (!!last && last.author.id === myId);
          const preview = messagePreviewText(last, { selfId: myId, withAuthor });
          // Someone (not me) is typing here → a live "печатает…" line in place
          // of the last-message preview, the Telegram pattern.
          const someoneTyping = typingByDm[ch.id] ?? false;
          const time = last ? shortTimeLabel(last.createdAt) : '';
          const tags = tagsByChannel.get(ch.id);
          return (
            <DmRow
              key={ch.id}
              channelId={ch.id}
              label={label}
              avatarUrl={url}
              // 1:1 DM: seed the generated orb by the PEER's user id so a
              // photo-less peer shows the same colour as their message bubbles
              // (which use avatarOrbUrl(author.id)). Groups keep the channel id.
              fallbackSeed={ch.type === 'DM' && other ? other.userId : ch.id}
              active={active}
              time={time}
              preview={preview}
              someoneTyping={someoneTyping}
              tags={tags}
              unread={unreadByChannel[ch.id] ?? 0}
              mentions={mentionByChannel[ch.id] ?? 0}
              onSelect={selectDirectChannel}
              onOpenMenu={openMenuFor}
            />
          );
        })}
      </nav>
    );

  // The "Друзья" tab view — friend requests (accept/decline) + the full
  // roster. Replaces the old always-on bottom roster and the separate
  // friends modal/icon.
  const friendsView = (
    <div className="flex flex-col gap-4 px-1 pt-1">
      {incoming.length > 0 && (
        <FriendsSection title={t('dm.incomingRequests', { count: incoming.length })}>
          {incoming.map((r) => (
            <FriendRow
              key={r.user.id}
              friend={r}
              onOpen={() => void openDmWith(r.user.id)}
              actions={
                <>
                  <RowIconButton
                    label={t('common.accept')}
                    variant="accept"
                    disabled={pendingId === r.user.id}
                    onClick={() => accept(r.user.id)}
                  >
                    <UsersIcon className="h-4 w-4" />
                  </RowIconButton>
                  <RowIconButton
                    label={t('common.decline')}
                    variant="danger"
                    disabled={pendingId === r.user.id}
                    onClick={() => remove(r.user.id)}
                  >
                    <CloseIcon className="h-4 w-4" />
                  </RowIconButton>
                </>
              }
            />
          ))}
        </FriendsSection>
      )}
      {outgoing.length > 0 && (
        <FriendsSection title={t('dm.outgoingRequests', { count: outgoing.length })}>
          {outgoing.map((r) => (
            <FriendRow
              key={r.user.id}
              friend={r}
              actions={
                <RowIconButton
                  label={t('dm.cancelRequest')}
                  variant="danger"
                  disabled={pendingId === r.user.id}
                  onClick={() => remove(r.user.id)}
                >
                  <CloseIcon className="h-4 w-4" />
                </RowIconButton>
              }
            />
          ))}
        </FriendsSection>
      )}
      {sortedFriends.length > 0 ? (
        <FriendsSection title={t('dm.friendsCount', { count: sortedFriends.length })}>
          {sortedFriends.map((f) => (
            <FriendRow
              key={f.user.id}
              friend={f}
              onOpen={() => void openDmWith(f.user.id)}
              actions={
                <>
                  <RowIconButton label={t('dm.write')} variant="accept" onClick={() => void openDmWith(f.user.id)}>
                    <ChatBubbleIcon className="h-4 w-4" />
                  </RowIconButton>
                  <RowIconButton
                    label={t('dm.removeFriend')}
                    variant="danger"
                    disabled={pendingId === f.user.id}
                    onClick={() => remove(f.user.id)}
                  >
                    <CloseIcon className="h-4 w-4" />
                  </RowIconButton>
                </>
              }
            />
          ))}
        </FriendsSection>
      ) : incoming.length === 0 && outgoing.length === 0 ? (
        <p className="px-3 py-8 text-center text-sm text-muted">
          {t('dm.noFriendsHint')}
        </p>
      ) : null}
    </div>
  );

  return (
    <div className={isLeft ? 'flex h-full flex-row' : 'flex h-full flex-col'}>
      {strip}
      <div data-tour="dm-list" className="flex min-w-0 flex-1 flex-col">
        {!friendsTab && (
          <div data-tour="vault">
            <SavedShortcut active={!!vault && vault.id === currentChannelId} onClick={() => void openVault()} />
          </div>
        )}
        {friendsTab ? friendsView : list}
      </div>

      {menu && (
        <DmFolderContextMenu
          channelId={menu.channelId}
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
        />
      )}

      {folderMenu && (
        <FolderManageMenu
          folderId={folderMenu.id}
          folderName={folderMenu.name}
          x={folderMenu.x}
          y={folderMenu.y}
          onClose={() => setFolderMenu(null)}
          onDeleted={() => {
            if (activeFolderId === folderMenu.id) setActiveFolderId(null);
          }}
        />
      )}
    </div>
  );
}
